import { PlanEditHolder } from '@core/models/plan-edit.model';

import { PlanEditState, watchShowsLive } from './plan-edit.state';

export type PlanHolderKind =
  | 'own-here'
  | 'own-assistant'
  | 'own-elsewhere'
  | 'other'
  | 'other-assistant';

/** Whether the timings on screen are final: owed a recompute, or left provisional by a failed one. */
export type PlanHolderTimings = 'recomputing' | 'provisional' | null;

export interface PlanHolderView {
  kind: PlanHolderKind;
  name: string;
  /** The session's changes are on screen. */
  live: boolean;
  timings: PlanHolderTimings;
  /** The chat tab behind Open chat and Discard; only for this user's assistant in this browser. */
  assistantHold: PlanEditHolder | null;
}

export const HOLDER_KIND_KEYS: Record<PlanHolderKind, string> = {
  'own-here': 'own_here',
  'own-assistant': 'own_assistant',
  'own-elsewhere': 'own_elsewhere',
  other: 'other',
  'other-assistant': 'other_assistant'
};

/**
 * Once the watch has answered for the plan it is the truth, and a chat-tab hold it does not confirm
 * is stale. Until then (in flight, refused, or a GBE that predates watching) the tab hold still
 * names the assistant, without claiming its changes show. `flavour` is the flavour a data view
 * shows, live only if the session projects it; null on the plan page, whose findings are live
 * whatever the session projects. Timings follow the session alone: the recompute is the session's,
 * and a view's aux data may show the session's timings even when its own rows are stored.
 * On the tab editing the plan it is `own-here`, whatever the watch says: the holder the watch
 * reports is that tab, and the tab's edit bar shows the timings.
 */
export function planHolderView(
  state: PlanEditState,
  userId: number | undefined,
  planId: string | null,
  flavour: string | null
): PlanHolderView | null {
  if (planId === null) return null;
  const watch = state.watch;
  if (state.session.mode !== 'off' && state.session.planId === planId) {
    return {
      kind: 'own-here',
      name: '',
      live: watch.planId === planId && liveOn(state, flavour),
      timings: null,
      assistantHold: null
    };
  }
  const tabHold = state.holders[planId] ?? null;
  if (
    watch.planId !== planId ||
    watch.phase === 'subscribing' ||
    watch.phase === 'unavailable'
  ) {
    return tabHold
      ? {
          kind: 'own-assistant',
          name: '',
          live: false,
          timings: null,
          assistantHold: tabHold
        }
      : null;
  }
  const holder = watch.holder;
  if (holder === null) return null;
  const own = userId !== undefined && holder.userId === userId;
  const kind: PlanHolderKind = own
    ? holder.via === 'assistant' && tabHold
      ? 'own-assistant'
      : 'own-elsewhere'
    : holder.via === 'assistant'
      ? 'other-assistant'
      : 'other';
  const isLive = watch.phase === 'live';
  const live = liveOn(state, flavour);
  return {
    kind,
    name: holder.name,
    live,
    timings: !isLive
      ? null
      : watch.recomputeFailed
        ? 'provisional'
        : watch.recomputePending
          ? 'recomputing'
          : null,
    assistantHold: kind === 'own-assistant' ? tabHold : null
  };
}

function liveOn(state: PlanEditState, flavour: string | null): boolean {
  return flavour === null
    ? state.watch.phase === 'live'
    : watchShowsLive(state.watch, flavour);
}
